import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { putEvent } from '../api/events';
import { StreamKeyInput, buildKeyPayload, isKeyComplete } from './StreamKeyInput';

interface StreamKey {
  fields?: string[];
  delimiter?: string;
}

interface Props {
  storeName: string;
  defaultPartitionKey?: string;
  onSuccess?: () => void;
  streamKey?: StreamKey;
}

export function WriteEventForm({ storeName, defaultPartitionKey, onSuccess, streamKey }: Props) {
  const [stream, setStream] = useState(defaultPartitionKey || '');
  const [keyFields, setKeyFields] = useState<Record<string, string>>({});
  const [eventType, setEventType] = useState('');
  const [dataJson, setDataJson] = useState('{\n  \n}');
  const [correlationId, setCorrelationId] = useState('');
  const [expectedVersion, setExpectedVersion] = useState('');
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      const data = JSON.parse(dataJson);
      const metadata: Record<string, unknown> = { eventType };
      if (correlationId) metadata.correlationId = correlationId;
      const expVer = expectedVersion ? parseInt(expectedVersion) : undefined;
      const keyPayload = buildKeyPayload(streamKey, stream, keyFields);
      return putEvent(storeName, keyPayload, data, metadata as { eventType: string; correlationId?: string }, expVer);
    },
    onSuccess: (result) => {
      const resultData = result.data as Record<string, unknown>;
      setFlash([{
        type: 'success',
        content: `Event written. Version: ${resultData.version}, EventId: ${resultData.eventId}`,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }]);
      queryClient.invalidateQueries({ queryKey: ['events', storeName] });
      onSuccess?.();
    },
    onError: (err: Error) => {
      setFlash([{
        type: 'error',
        content: err.message,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }]);
    },
  });

  const isValidJson = () => {
    try {
      JSON.parse(dataJson);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <ExpandableSection headerText="Write event" variant="footer">
      <SpaceBetween size="m">
        <Flashbar items={flash} />
        <ColumnLayout columns={2}>
          <StreamKeyInput
            streamKey={streamKey}
            stream={stream}
            onStreamChange={setStream}
            keyFields={keyFields}
            onKeyFieldsChange={setKeyFields}
          />
          <FormField label="Event type" constraintText="Required">
            <Input
              value={eventType}
              onChange={({ detail }) => setEventType(detail.value)}
              placeholder="OrderCreated"
            />
          </FormField>
        </ColumnLayout>
        <FormField
          label="Data (JSON)"
          constraintText="Required — must be valid JSON"
          errorText={!isValidJson() && dataJson.trim() ? 'Invalid JSON' : undefined}
        >
          <Textarea
            value={dataJson}
            onChange={({ detail }) => setDataJson(detail.value)}
            rows={6}
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField label="Correlation ID" constraintText="Optional">
            <Input
              value={correlationId}
              onChange={({ detail }) => setCorrelationId(detail.value)}
              placeholder="txn-001"
            />
          </FormField>
          <FormField label="Expected version" constraintText="Optional — for conditional writes">
            <Input
              value={expectedVersion}
              onChange={({ detail }) => setExpectedVersion(detail.value)}
              placeholder="Leave empty for unconditional"
              type="number"
            />
          </FormField>
        </ColumnLayout>
        <Button
          variant="primary"
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
          disabled={!isKeyComplete(streamKey, stream, keyFields) || !eventType || !isValidJson()}
        >
          Write event
        </Button>
      </SpaceBetween>
    </ExpandableSection>
  );
}
